/**
 * useVotes (CHAP-85) - list votes + tallies + cast/create/close mutations.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost } from '../lib/api.js';

export type VoteMode = 'attributed' | 'anonymous';
export type VoteStatus = 'open' | 'closed' | 'tabled';

export interface VoteListItem {
  id: string;
  title: string;
  description: string | null;
  options: string[];
  mode: VoteMode;
  status: VoteStatus;
  deadline: string | null;
  tally: Record<string, number>;
  totalResponses: number;
  hasVoted: boolean;
  createdAt: string;
}

export interface VotesResponse {
  votes: VoteListItem[];
}

export function useVotes(slug: string) {
  return useQuery<VotesResponse, Error>({
    queryKey: ['votes', slug],
    queryFn: () => apiGet<VotesResponse>(`/api/c/${slug}/votes`),
    enabled: !!slug,
  });
}

export interface CreateVoteBody {
  title: string;
  description?: string;
  options: string[];
  mode: VoteMode;
  deadline?: string;
}

export function useCreateVote(slug: string) {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean; vote: VoteListItem }, Error, CreateVoteBody>({
    mutationFn: (body) => apiPost(`/api/c/${slug}/votes`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['votes', slug] }),
  });
}

export function useCastVote(slug: string) {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean; response: { optionValue: string } }, Error, { id: string; optionValue: string }>({
    mutationFn: ({ id, optionValue }) => apiPost(`/api/c/${slug}/votes/${id}/cast`, { optionValue }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['votes', slug] }),
  });
}

export function useSetVoteStatus(slug: string) {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean; vote: { id: string; status: VoteStatus } }, Error, { id: string; status: VoteStatus }>({
    mutationFn: ({ id, status }) => apiPatch(`/api/c/${slug}/votes/${id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['votes', slug] }),
  });
}
